import { useCallback, useLayoutEffect, useEffect, useState } from 'react'
import { GetCard } from '@web/walter-lib/api'
import { useSWRConfig } from 'swr'
import { useSearchParams } from 'react-router-dom'
import { restKeys } from '~api/swr/keys'
import { QueryParams } from '~constants/routes'
import { useCards } from '~api'

export const useFirstSelectedCard = (
    setSelectedId: (id: string) => void,
    selectedCard?: GetCard,
    sortedCards?: GetCard[]
) => {
    const [searchParams] = useSearchParams()
    const action = searchParams.get(QueryParams.action) || ''
    const currency = searchParams.get(QueryParams.currency) || ''
    const queryCardId = searchParams.get(QueryParams.cardId) || ''
    const { data: cards } = useCards()

    // eslint-disable-next-line complexity
    useLayoutEffect(() => {
        if (selectedCard || !sortedCards) {
            return
        }

        const queryCardIndex = sortedCards?.findIndex(card => card.id === queryCardId) || -1
        if (queryCardIndex > -1) {
            const queryCard = sortedCards[queryCardIndex]

            setSelectedId(queryCard.id)

            const renderedRow = document.querySelector(
                `div[data-pointer='card_list_table'] div[data-pointer='table_row_${queryCardIndex}']`
            )
            renderedRow?.scrollIntoView()

            return
        }

        const prevCardId = localStorage.getItem('cards_selectedCard')
        const prevSelectCardIndex = sortedCards?.findIndex(card => card.id === prevCardId) || -1
        const prevSelectCard = prevSelectCardIndex > -1 ? sortedCards?.[prevSelectCardIndex] : undefined

        let cardIndex = prevSelectCardIndex

        if (prevSelectCard) {
            if (action === 'topup') {
                if (
                    prevSelectCard?.status === 'ACTIVE' &&
                    (!currency || prevSelectCard?.currency?.toUpperCase() === currency?.toUpperCase())
                ) {
                    setSelectedId(prevSelectCard?.id)
                } else {
                    const activeCards = cards?.cards?.filter(
                        card =>
                            card?.status === 'ACTIVE' &&
                            (!currency || card?.currency?.toUpperCase() === currency?.toUpperCase())
                    )

                    if (activeCards?.length) {
                        setSelectedId(activeCards[0]?.id)
                        cardIndex = sortedCards?.findIndex(card => card.id === activeCards[0]?.id)
                    } else {
                        setSelectedId(prevSelectCard?.id)
                    }
                }
            } else {
                setSelectedId(prevSelectCard?.id)
            }

            const renderedRow = document.querySelector(
                `div[data-pointer='card_list_table'] div[data-pointer='table_row_${cardIndex}']`
            )
            renderedRow?.scrollIntoView()
        } else {
            sortedCards?.[0] && setSelectedId(sortedCards?.[0]?.id)
        }
    }, [action, cards?.cards, currency, selectedCard, setSelectedId, sortedCards, queryCardId])
}

export const useMemorizeCard = (selectedCard?: GetCard) => {
    useEffect(() => {
        localStorage.setItem('cards_selectedCard', selectedCard?.id || '')
    }, [selectedCard])
}

export const useCloseTopUp = (setTopUpModal: (isOpen: boolean) => void, resetTx: (trigger: number) => void) => {
    const { mutate } = useSWRConfig()

    const resetTxCb = useCallback(async () => {
        await mutate(key => {
            return (key as { key?: string })?.key === restKeys.getTransactionsCardsHistory ? key : null
        }, undefined)
        resetTx(Date.now())
    }, [mutate, resetTx])

    const onClose = useCallback(() => {
        resetTxCb?.()
        setTopUpModal(false)
    }, [resetTxCb, setTopUpModal])

    return { onClose }
}

export const usePreOpenTopUp = (setTopUpModal: (isOpen: boolean) => void, selectedCard?: GetCard) => {
    const [isInitialOpen, setInitialOpen] = useState(false)
    const [searchParams, setSearchParams] = useSearchParams()
    const action = searchParams.get(QueryParams.action) || ''
    const currency = searchParams.get(QueryParams.currency) || ''

    const { data: cards } = useCards()

    useEffect(() => {
        if (!selectedCard || isInitialOpen || action !== 'topup') {
            return
        }

        const isExist = cards?.cards?.some(
            card =>
                card?.status === 'ACTIVE' && (!currency || card?.currency?.toUpperCase() === currency?.toUpperCase())
        )

        if (isExist) {
            setTopUpModal(true)
            setInitialOpen(true)

            setSearchParams(
                prev => {
                    prev.delete(QueryParams.action)
                    prev.delete(QueryParams.currency)
                    prev.delete(QueryParams.cardId)
                    return prev
                },
                { replace: true }
            )
        }
    }, [action, cards?.cards, currency, isInitialOpen, selectedCard, setSearchParams, setTopUpModal])
}
